import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMES = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00"];
const todayStr = () => new Date().toISOString().slice(0, 10);

// mode: "member" (no member select) | "admin" (member select shown)
export default function BookingModal({ open, onOpenChange, mode = "admin", defaultDate, defaultTrainerId, defaultTime, onBooked }) {
  const [services, setServices] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ service_id: "", trainer_id: "", member_id: "", date: defaultDate || todayStr(), start_time: defaultTime || "18:00" });
  const [saving, setSaving] = useState(false);
  const [fullError, setFullError] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.active)));
    api.get("/trainers").then((r) => setTrainers(r.data));
    if (mode === "admin") api.get("/members").then((r) => setMembers(r.data));
    setForm((f) => ({ ...f, date: defaultDate || todayStr(), trainer_id: defaultTrainerId || f.trainer_id, start_time: defaultTime || f.start_time }));
    setFullError(false);
  }, [open, mode, defaultDate, defaultTrainerId, defaultTime]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { service_id: form.service_id, trainer_id: form.trainer_id, date: form.date, start_time: form.start_time };
      if (mode === "admin") payload.member_id = form.member_id;
      await api.post("/appointments", payload);
      toast.success("Randevu oluşturuldu");
      onOpenChange(false);
      setForm({ service_id: "", trainer_id: "", member_id: "", date: defaultDate || todayStr(), start_time: "18:00" });
      onBooked && onBooked();
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail);
      if (detail.toLowerCase().includes("kapasite")) setFullError(true);
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const joinWaitlist = async () => {
    setSaving(true);
    try {
      const payload = { service_id: form.service_id, date: form.date, start_time: form.start_time };
      if (mode === "admin") payload.member_id = form.member_id;
      const r = await api.post("/appointments/waitlist", payload);
      toast.success(`Bekleme listesine eklendin (sıran: ${r.data.position}). Yer açıldığında bildirim alacaksın.`);
      setFullError(false);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const valid = form.service_id && form.trainer_id && form.date && form.start_time && (mode === "member" || form.member_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><CalendarPlus size={20} className="text-[#FF5500]" /> Randevu Al</DialogTitle>
          <DialogDescription className="text-[#68687B]">Hizmet, antrenör ve saat seçerek randevu oluştur.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {mode === "admin" && (
            <Sel label="Üye" value={form.member_id} onChange={(v) => setForm({ ...form, member_id: v })} testId="booking-member" placeholder="Üye seç"
              options={members.map((m) => ({ value: m.id, label: `${m.name}${m.remaining_lessons != null ? ` · ${m.remaining_lessons} ders` : ""}` }))} />
          )}
          <Sel label="Hizmet" value={form.service_id} onChange={(v) => setForm({ ...form, service_id: v })} testId="booking-service" placeholder="Hizmet seç"
            options={services.map((s) => ({ value: s.id, label: `${s.name} (${s.duration}dk)` }))} />
          <Sel label="Antrenör" value={form.trainer_id} onChange={(v) => setForm({ ...form, trainer_id: v })} testId="booking-trainer" placeholder="Antrenör seç"
            options={trainers.map((t) => ({ value: t.id, label: t.name }))} />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#A0A0B2] mb-1 block">Tarih</label>
              <Input type="date" value={form.date} min={todayStr()} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="booking-date" className="bg-[#0E0E14] border-[#262636] text-white [color-scheme:dark]" /></div>
            <Sel label="Saat" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="booking-time" options={TIMES.map((t) => ({ value: t, label: t }))} />
          </div>
        </div>
        {fullError && (
          <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-3 flex items-center justify-between gap-3" data-testid="booking-full-banner">
            <p className="text-sm text-[#F59E0B]">Bu saat dolu. Yer açılırsa haber verelim mi?</p>
            <Button size="sm" onClick={joinWaitlist} disabled={saving} data-testid="booking-waitlist-button"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold flex-shrink-0 text-xs">
              {saving ? <Loader2 className="animate-spin" size={14} /> : "Bekleme Listesine Katıl"}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0A0B2]">İptal</Button>
          <Button onClick={submit} disabled={saving || !valid} data-testid="booking-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Randevu Oluştur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Sel({ label, value, onChange, options, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs text-[#A0A0B2] mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white max-h-64">
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
